import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StationMeta } from '@tideprecision/shared';
import { apiGetTide, apiGetTideByStation, prefetchAllStations } from './api.js';
import { AlmanacBadges } from './components/AlmanacBadges.js';
import { DateNav } from './components/DateNav.js';
import { ExtremaList } from './components/ExtremaList.js';
import { InstallBanner } from './components/InstallBanner.js';
import { LocateButton } from './components/LocateButton.js';
import { OfflineBadge } from './components/OfflineBadge.js';
import { StationPicker } from './components/StationPicker.js';
import { TideStatusCard } from './components/TideStatusCard.js';
import { WindForecastPanel } from './components/WindForecastPanel.js';
import { getCurrentLocation } from './lib/geolocation.js';
import {
  filterWindHoursByDate,
  findWindHourByMinute,
  loadWindForecast,
  summarizeWindByPeriods,
  type WindForecastResult,
} from './lib/wind.js';
import { getSetting } from './state/idb.js';
import { LAST_STATION_KEY, todayJst, useStore } from './state/store.js';

// Recharts は重い (~400KB)。初期表示には不要なので遅延 import で別チャンクに分離する。
const TideChart = lazy(() => import('./components/TideChart.js').then((m) => ({ default: m.TideChart })));

// React StrictMode (dev) の二重マウントで /api/session + /api/tide が二重呼びされるのを防ぐ。
// モジュールレベルなのでリロード時は確実にリセットされ、StrictMode 再マウントだけスキップする。
let initialFetchStarted = false;

function App(): JSX.Element {
  const tideData = useStore((s) => s.tideData);
  const date = useStore((s) => s.date);
  const isLoading = useStore((s) => s.isLoading);
  const error = useStore((s) => s.error);
  const selectedStation = useStore((s) => s.selectedStation);
  const refreshKey = useStore((s) => s.refreshKey);

  const setSelectedStation = useStore((s) => s.setSelectedStation);
  const setTideData = useStore((s) => s.setTideData);
  const setIsLoading = useStore((s) => s.setIsLoading);
  const setError = useStore((s) => s.setError);
  const setIsOffline = useStore((s) => s.setIsOffline);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [windResult, setWindResult] = useState<WindForecastResult | null>(null);
  const [isWindLoading, setIsWindLoading] = useState(false);
  const [windError, setWindError] = useState<string | null>(null);

  // オンライン/オフライン監視
  useEffect(() => {
    const onlineH = () => setIsOffline(false);
    const offlineH = () => setIsOffline(true);
    window.addEventListener('online', onlineH);
    window.addEventListener('offline', offlineH);
    return () => {
      window.removeEventListener('online', onlineH);
      window.removeEventListener('offline', offlineH);
    };
  }, [setIsOffline]);

  // 初回ロード: 前回地点があれば復元、なければ Geolocation で最寄りを取得
  useEffect(() => {
    if (initialFetchStarted) return;
    initialFetchStarted = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const last = await getSetting<StationMeta>(LAST_STATION_KEY);
        const r = last
          ? await apiGetTideByStation(last.id, todayJst())
          : await (async () => {
              const coords = await getCurrentLocation();
              return apiGetTide(coords.lat, coords.lon, todayJst());
            })();
        setTideData(r);
        setSelectedStation({
          id: r.station.id,
          name: r.station.name,
          name_kana: r.station.name_kana,
          prefecture: r.station.prefecture,
          latitude: r.station.latitude,
          longitude: r.station.longitude,
        });
      } catch (e) {
        setError(`データ取得に失敗: ${String(e)}`);
      } finally {
        setIsLoading(false);
      }

      // 完全オフライン化: 全地点 BLOB をバックグラウンドで IDB にプリフェッチ。
      // 主画面の描画を阻害しないよう idle で実行。1 日 1 回まで（IDB の setting で制御）。
      const triggerPrefetch = () => {
        void prefetchAllStations().catch(() => {
          /* failures are silent */
        });
      };
      const w = window as unknown as { requestIdleCallback?: (cb: () => void) => void };
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(triggerPrefetch);
      } else {
        setTimeout(triggerPrefetch, 2000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 日付・地点変更時の再取得
  const refreshTide = useCallback(async () => {
    if (!selectedStation) return;
    setIsLoading(true);
    setError(null);
    try {
      const r = await apiGetTideByStation(selectedStation.id, date);
      setTideData(r);
    } catch (e) {
      setError(`データ取得に失敗: ${String(e)}`);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStation, date, setIsLoading, setError, setTideData]);

  // 「同じ地点・同じ日付」のままでも更新ボタンで強制再フェッチできるよう
  // refreshKey の変化を ref で検知する。
  const lastSeenRefreshKey = useRef(0);
  useEffect(() => {
    if (!tideData) return; // 初回ロード時は別の useEffect が処理
    if (!selectedStation) return;
    const stale =
      tideData.station.id !== selectedStation.id || tideData.date !== date;
    const refreshFired = refreshKey !== lastSeenRefreshKey.current;
    lastSeenRefreshKey.current = refreshKey;
    if (stale || refreshFired) {
      void refreshTide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation?.id, date, refreshKey]);

  useEffect(() => {
    if (!selectedStation) {
      setWindResult(null);
      setWindError(null);
      setIsWindLoading(false);
      return;
    }

    let cancelled = false;
    setWindResult((current) =>
      current?.forecast.stationId === selectedStation.id ? current : null,
    );
    setIsWindLoading(true);
    setWindError(null);
    loadWindForecast(selectedStation)
      .then((next) => {
        if (!cancelled) setWindResult(next);
      })
      .catch((e) => {
        if (!cancelled) setWindError(String(e));
      })
      .finally(() => {
        if (!cancelled) setIsWindLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedStation]);

  const refreshWind = useCallback(
    async (forceRefresh = false) => {
      if (!selectedStation) return;
      setIsWindLoading(true);
      setWindError(null);
      try {
        const next = await loadWindForecast(selectedStation, { forceRefresh });
        setWindResult(next);
      } catch (e) {
        setWindError(String(e));
      } finally {
        setIsWindLoading(false);
      }
    },
    [selectedStation],
  );

  const currentMinute = useMemo(() => {
    if (!tideData) return null;
    if (tideData.date !== todayJst()) return null;
    const now = new Date();
    const jstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    const m = jstNow.getUTCHours() * 60 + jstNow.getUTCMinutes();
    return m;
  }, [tideData]);

  const windRowsForSelectedDate = useMemo(
    () => (windResult ? filterWindHoursByDate(windResult.forecast, date) : []),
    [windResult, date],
  );

  const tideWindRows = useMemo(() => {
    if (!tideData || !windResult) return [];
    if (windResult.forecast.stationId !== tideData.station.id) return [];
    return filterWindHoursByDate(windResult.forecast, tideData.date);
  }, [tideData, windResult]);

  const currentWind = useMemo(
    () => (currentMinute !== null ? findWindHourByMinute(tideWindRows, currentMinute) : null),
    [currentMinute, tideWindRows],
  );

  const windSummary = useMemo(
    () => (currentMinute === null ? summarizeWindByPeriods(tideWindRows) : []),
    [currentMinute, tideWindRows],
  );

  return (
    <div className="min-h-full p-4 sm:p-6 max-w-3xl mx-auto space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          🌊 <span className="text-sky-400">シンプル</span>潮汐
        </h1>
        <div className="flex items-center gap-2">
          <LocateButton />
          <OfflineBadge />
        </div>
      </header>

      <InstallBanner />

      {selectedStation && (
        <section className="rounded bg-slate-800/60 border border-slate-700/40 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-400">現在の地点</div>
              <div className="text-lg font-bold">
                {selectedStation.name}
                <span className="ml-2 text-sm text-slate-400">
                  {selectedStation.prefecture ?? ''}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 text-sm"
            >
              {pickerOpen ? '閉じる' : '地点切替'}
            </button>
          </div>
          {pickerOpen && (
            <div className="mt-3">
              <StationPicker
                onSelect={(s) => {
                  setSelectedStation(s);
                  setPickerOpen(false);
                }}
              />
            </div>
          )}
        </section>
      )}

      {/* 暦情報バッジ列 (v1.1) */}
      <section className="rounded bg-slate-800/40 border border-slate-700/30 p-3">
        <AlmanacBadges date={date} />
      </section>

      {/* 潮汐ステータス: 当日は現在潮位+動き、当日以外は最高/最低/潮差サマリ。
          グラフより先に視認できるよう上に配置する。 */}
      {tideData && (
        <TideStatusCard
          data={tideData}
          currentMinute={currentMinute}
          currentWind={currentWind}
          windSummary={windSummary}
        />
      )}

      <section className="rounded bg-slate-800/60 border border-slate-700/40 p-4 space-y-3">
        <DateNav />
        {error && <div className="text-rose-400 text-sm">{error}</div>}
        {tideData ? (
          // 既にデータがあるときは isLoading 中もグラフを残し、
          // 「更新中…」をオーバーレイ表示する。レイアウトを動かさず再フェッチによる
          // ちらつきを防ぐ。
          <div className="relative">
            <Suspense
              fallback={
                <div className="h-72 flex items-center justify-center text-slate-500 text-sm">
                  グラフを準備中…
                </div>
              }
            >
              <TideChart
                data={tideData}
                currentMinute={currentMinute}
                windHours={tideWindRows}
              />
            </Suspense>
            {isLoading && (
              <div className="absolute top-2 right-2 text-xs text-slate-200 bg-slate-900/80 px-2 py-1 rounded shadow pointer-events-none">
                更新中…
              </div>
            )}
          </div>
        ) : (
          isLoading && (
            <div className="h-72 flex items-center justify-center text-slate-500 text-sm">
              読み込み中…
            </div>
          )
        )}
      </section>

      {selectedStation && (
        <WindForecastPanel
          station={selectedStation}
          date={date}
          result={windResult}
          rows={windRowsForSelectedDate}
          isLoading={isWindLoading}
          error={windError}
          onRefresh={() => void refreshWind(true)}
        />
      )}

      {tideData && tideData.extrema.length > 0 && (
        <section className="rounded bg-slate-800/60 border border-slate-700/40 p-4">
          <div className="text-xs text-slate-400 mb-2">満潮・干潮</div>
          <ExtremaList data={tideData} />
        </section>
      )}

      <footer className="text-xs text-slate-500 pt-4 border-t border-slate-800 space-y-1">
        <p>
          気象庁の潮位推算値（2025 年・全国 239 地点）から自前の調和分析で抽出した 30 分潮の調和定数による計算結果です（精度 RMS &lt; 5cm）。
        </p>
        <p>
          出典:{' '}
          <a
            href="https://www.data.jma.go.jp/kaiyou/db/tide/suisan/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-300"
          >
            気象庁ホームページ 潮位表
          </a>
        </p>
        <p>気象擾乱（台風・気圧・風）による実測との差は補正されません。</p>
        <p>
          風予報: Weather data by{' '}
          <a
            href="https://www.met.no/en"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-300"
          >
            MET Norway（ノルウェー気象庁）
          </a>
          （
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-300"
          >
            CC BY 4.0
          </a>
          ）
        </p>
      </footer>
    </div>
  );
}

export default App;
